// The review's content, liftable (R9): the package card at its
// confirmed prices, then ONE answers card holding every walk answer —
// grouped by the screens' own section labels, in walk order, so a new
// screen or question lands on the review with no curation here. The
// juncture's review screen wraps this with its heading and actions; a
// later what-did-I-agree-to modal renders it read-only by passing no
// change handlers.
//
// Answer values and the insured's name carry `ph-no-capture`: session
// replay never records them, matching the walk's inputs, which PostHog
// masks by default. Question titles and section labels stay readable.

import { CheckIcon } from "@heroicons/react/24/outline";

import type { QuotePaymentOption, QuotePlan } from "./api";
import { dollars, monthlyScheduleValue, optionLabel } from "./payment-copy";
import type { AnswerRow, ReviewSectionGroup } from "./review-answers";
import SocialProofBand from "./SocialProofBand";

/** Client-bundled carrier marks, keyed by the carrier's stable slug. A
 * carrier without one renders as a wordmark — never a broken image. */
const CARRIER_LOGOS: Record<string, string> = {
  global_atlantic: "/public/carrier-global-atlantic.png",
  security_national_life: "/public/carrier-security-national.webp",
  fgic: "/public/carrier-fgic.png",
};

const CHANGE_LINK_CLASS =
  "flex-none bg-transparent p-0 text-base font-bold text-evergreen underline underline-offset-4 transition-colors hover:text-plum";

function presentRows(rows: (AnswerRow | null)[]): AnswerRow[] {
  return rows.filter(
    (row): row is AnswerRow => row !== null && row.value !== "",
  );
}

function DetailRows({ rows }: { rows: AnswerRow[] }) {
  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex flex-col gap-0.5 border-t border-oat-100 px-5 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-5"
        >
          <span className="flex-none text-base text-oat-700">{row.label}</span>
          <span className="text-base font-bold text-soil sm:text-right">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A block's sub-head. The card header (the tinted band) already reads
 * as a divider, so the first block directly under IT renders flush; the
 * plain group heads inside the card keep the bordered sub-head below
 * them, like every other section boundary. */
function SectionHead({
  children,
  flush,
}: {
  children: string;
  flush?: boolean;
}) {
  return (
    <div
      className={`px-5 pb-2 text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700 ${
        flush ? "pt-2" : "mt-0.5 border-t border-oat-100 pt-4"
      }`}
    >
      {children}
    </div>
  );
}

/** Answer rows: a label or question takes the row's width — sentence-
 * length questions wrap — and the answer stands bold beside it. */
function AnswerRows({ rows }: { rows: AnswerRow[] }) {
  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex flex-col gap-0.5 border-t border-oat-100 px-5 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-5"
        >
          <span className="min-w-0 flex-1 text-base leading-snug text-oat-700">
            {row.label}
          </span>
          <span className="ph-no-capture min-w-0 text-base font-bold text-soil sm:max-w-[60%] sm:flex-none sm:text-right">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface ReviewContentProps {
  planName: string;
  planFeatures: QuotePlan["features"];
  /** The confirmed option, at the amounts the juncture blessed. */
  option: QuotePaymentOption;
  planningFor: "self" | "loved_one";
  /** Named under the answers card's opening header. */
  insuredName: string;
  /** The policy's market — picks the testimonial a neighbor wrote. */
  marketSlug: string | null;
  /** The walk's answers grouped by the screens' sections, in walk order
   * (see `sectionGroups`) — the review renders exactly these. */
  sections: ReviewSectionGroup[];
  /** Absent handlers render everything without Change links — the
   * read-only lift. A section's Change lands on its first screen. */
  onChangePlan?: () => void;
  onChangeSection?: (screenRef: string) => void;
}

export default function ReviewContent({
  planName,
  planFeatures,
  option,
  planningFor,
  insuredName,
  marketSlug,
  sections,
  onChangePlan,
  onChangeSection,
}: ReviewContentProps) {
  const forLoved = planningFor === "loved_one";
  const carrierLogo = CARRIER_LOGOS[option.carrier_id];

  const planRows = presentRows([
    { label: "How you're paying", value: optionLabel(option) },
    { label: "Due today", value: dollars(option.due_today_cents) },
    {
      label: "Then each month",
      value:
        option.recurring_cents !== null
          ? monthlyScheduleValue(option, option.recurring_cents)
          : "Nothing — paid in full",
    },
  ]);

  return (
    <div className="flex flex-col">
      <div className="overflow-hidden rounded-2xl border border-oat-200 bg-white shadow-sm">
        <div className="flex items-end justify-between gap-4 bg-oat-50 px-5 py-4 sm:px-5 sm:py-5">
          <div>
            <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
              Your package
            </span>
            <h2 className="mt-1 font-serif text-[25px] leading-tight text-soil">
              {planName} <span className="text-oat-700">package</span>
            </h2>
          </div>
          {onChangePlan && (
            <button
              type="button"
              className={CHANGE_LINK_CLASS}
              onClick={onChangePlan}
            >
              Change
            </button>
          )}
        </div>
        <DetailRows rows={planRows} />
        {planFeatures.length > 0 && (
          <>
            <SectionHead>What&rsquo;s included</SectionHead>
            <ul className="grid grid-cols-1 gap-2.5 px-5 pb-5 pt-1 sm:grid-cols-2 sm:gap-x-7">
              {planFeatures.map((feature) => (
                <li key={feature.label} className="flex items-start gap-2.5">
                  <CheckIcon
                    className="mt-0.5 h-4 w-4 shrink-0 text-evergreen"
                    strokeWidth={3}
                    aria-hidden="true"
                  />
                  <span className="text-base leading-snug text-soil">
                    {feature.label}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {sections.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-oat-200 bg-white shadow-sm">
          {sections.map((section, index) => (
            <div key={section.screenRefs[0]}>
              {index === 0 ? (
                // The answers card opens the way the package card does —
                // the first group's label as the kicker, the insured's
                // name as the headline.
                <div className="flex items-end justify-between gap-4 bg-oat-50 px-5 py-4 sm:px-5 sm:py-5">
                  <div>
                    <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
                      {section.label}
                    </span>
                    {insuredName !== "" && (
                      <h2 className="ph-no-capture mt-1 font-serif text-[25px] leading-tight text-soil">
                        {insuredName}
                      </h2>
                    )}
                  </div>
                  {onChangeSection && (
                    <button
                      type="button"
                      className={CHANGE_LINK_CLASS}
                      onClick={() => onChangeSection(section.screenRefs[0])}
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-end justify-between gap-4 border-t border-oat-100 px-5 pb-2 pt-4">
                  <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
                    {section.label}
                  </span>
                  {onChangeSection && (
                    <button
                      type="button"
                      className={CHANGE_LINK_CLASS}
                      onClick={() => onChangeSection(section.screenRefs[0])}
                    >
                      Change
                    </button>
                  )}
                </div>
              )}
              {section.blocks.map((block, blockIndex) => (
                <div key={`${section.screenRefs[0]}-${blockIndex}`}>
                  {block.heading !== null && (
                    <SectionHead flush={index === 0 && blockIndex === 0}>
                      {block.heading}
                    </SectionHead>
                  )}
                  <AnswerRows rows={block.rows} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-col items-center gap-5 rounded-xl bg-oat p-5 sm:flex-row sm:gap-6 sm:px-6">
        <div className="flex h-[104px] w-[104px] flex-none items-center justify-center rounded-lg border border-oat-200 bg-white p-3.5 sm:h-[116px] sm:w-[116px]">
          {carrierLogo ? (
            <div
              role="img"
              aria-label={option.carrier_name}
              className="h-full w-full bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${carrierLogo})` }}
            />
          ) : (
            <span className="text-center text-[17px] font-bold leading-tight tracking-tight text-soil">
              {option.carrier_name}
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
            We&rsquo;re proud to partner with
          </span>
          <span className="font-serif text-[22px] leading-tight text-soil">
            {option.carrier_name}
          </span>
          <p className="text-base leading-[1.55] text-oat-700">
            {option.carrier_name} is a licensed life insurance carrier.{" "}
            {forLoved && insuredName ? (
              <span className="ph-no-capture">{insuredName}&rsquo;s</span>
            ) : (
              "Your"
            )}{" "}
            pre-need policy is issued and funded through them.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <SocialProofBand marketSlug={marketSlug} />
      </div>
    </div>
  );
}
